import { createSelector } from 'reselect';
import { includes, isEmpty, isNaN, isNil, omitBy, overSome, toNumber, toSafeInteger } from 'lodash/fp';
import qs from 'qs';

import { INCOME_TIER_NONE } from '../../components/pricing-drawer/constants';
import constants from '../../ahm-constants';
import getIncomeBracket from '../../utils/get-income-bracket';
import getMemberAge from '../../utils/get-member-age';
import hasHospitalComponent from '../../utils/has-hospital-component';
import selectAgeBracket from '../select-age-bracket';
import selectAgeBracketOption from '../select-age-bracket-option';
import selectCalculatedYouthDiscountPercentage from '../select-calculated-youth-discount-percentage';
import selectCart from '../select-cart';
import selectCartPrice from '../select-cart-price';
import selectCheckout from '../select-checkout';
import selectCustomer from '../select-customer';
import selectLocation from '../select-location';
import selectMembership from '../select-membership';
import selectRebatePercentage from '../select-rebate-percentage';

// @ts-expect-error - Automatic, Please fix when editing this file
const selectLeadForm = (state, leadFormData) => leadFormData;

// @ts-expect-error - Automatic, Please fix when editing this file
const stringifyQuery = ({ age, frequency, hospitalExcess, incomeTier, incomeType, partnerAge, scale, state }) => {
  const finalQuery = qs.stringify(
    {
      age,
      frequency,
      hospitalExcess,
      incomeTier,
      incomeType,
      partnerAge,
      scale,
      state,
    },
    { skipNulls: true },
  );

  return finalQuery !== '' ? `?${finalQuery}` : '';
};

// @ts-expect-error - Automatic, Please fix when editing this file
const isExcessApplicable = (input, productUrl) => includes(input, productUrl);

const selectLeadCapture = createSelector(
  [
    selectAgeBracket,
    selectAgeBracketOption,
    selectCalculatedYouthDiscountPercentage,
    selectCartPrice,
    selectCart,
    selectCheckout,
    selectCustomer,
    selectLocation,
    selectLeadForm,
    selectMembership,
    selectRebatePercentage,
  ],
  (
    ageBracket,
    ageBracketOption,
    calculatedYouthDiscountPercentage,
    cartPrice,
    cart,
    checkout,
    customer,
    location,
    leadFormData,
    membership,
    rebatePercentage,
  ) => {
    const { PRODUCT_TYPE, RELATIONSHIP, SCALE, URLS } = constants;

    const age = toSafeInteger(getMemberAge({ customer, relationship: RELATIONSHIP.CLIENT })) || null;
    const hospitalExcess = customer?.hospitalExcess;
    const rebate = customer?.rebate;
    const incomeTier = rebate ? customer?.incomeTier : INCOME_TIER_NONE;
    const incomeType = customer?.incomeType === SCALE.SINGLE ? `${SCALE.SINGLE}` : `${SCALE.FAMILY}`;
    const frequency = customer?.frequency;
    const leadLocation = leadFormData?.location ?? null;
    const partnerAge = toSafeInteger(getMemberAge({ customer, relationship: RELATIONSHIP.SPOUSE })) || null;
    const scale = customer?.scale;
    const state = customer?.state;
    const queryParams = {
      age,
      frequency,
      ...(isExcessApplicable(PRODUCT_TYPE.HOSPITAL, leadLocation || location) && {
        hospitalExcess,
      }),
      incomeTier,
      incomeType,
      partnerAge,
      scale,
      state,
    };

    const stringifiedQueries = `${stringifyQuery(queryParams)}&`;
    const url = leadLocation ? `${leadLocation}${stringifiedQueries}` : `${location}${stringifiedQueries}`;
    const getCartDetailsFromMembership =
      includes(URLS.SALES.BUY_SUCCESS, `/${url}`) && !isEmpty(membership?.membershipId);
    const price = cartPrice?.amount?.[frequency];
    const productType = getCartDetailsFromMembership ? membership?.cart?.product?.type : cart?.product?.type;
    const sendExcess = productType
      ? !includes(PRODUCT_TYPE.EXTRAS, productType)
      : isExcessApplicable(PRODUCT_TYPE.HOSPITAL, url);

    const getProductFromState = getCartDetailsFromMembership ? membership?.cart?.product : cart?.product;
    const isYouthDiscountApplicable = productType
      ? hasHospitalComponent(getProductFromState)
      : isExcessApplicable(PRODUCT_TYPE.HOSPITAL, url);
    const youthDiscountPercentage = isYouthDiscountApplicable ? calculatedYouthDiscountPercentage : null;
    const getDob = getCartDetailsFromMembership ? customer?.members?.[0]?.dob ?? null : checkout?.buy?.dob ?? null;
    const getPartnerDob = getCartDetailsFromMembership
      ? customer?.members?.[1]?.dob ?? null
      : checkout?.buy?.partnerDob ?? null;

    return omitBy(overSome([isNil, isNaN]), {
      age,
      dob: age && getDob,
      email: leadFormData?.email,
      excess: hospitalExcess && sendExcess ? toNumber(hospitalExcess) : null,
      frequency,
      income: customer?.income,
      incomeBracket: getIncomeBracket(incomeTier, customer?.rebate, incomeType),
      incomeTier,
      incomeType,
      isMember: leadFormData?.isMember ?? false,
      marketingOptIn: leadFormData?.marketingOptIn ?? false,
      name: leadFormData?.name,
      partnerAge,
      partnerDob: partnerAge && getPartnerDob,
      phone: leadFormData?.phone,
      price: price ? toNumber(price) : null,
      product: getProductFromState?.name,
      productType,
      rebateAgeBracket: ageBracketOption,
      rebateAgeTier: ageBracket,
      rebatePercentage,
      scale,
      state,
      url,
      youthDiscountPercentage,
    });
  },
);

export default selectLeadCapture;
